import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  FaServer, FaMicrochip, FaMemory, FaDatabase, FaSync, FaHistory,
  FaNetworkWired, FaListUl, FaTerminal, FaToggleOn, FaToggleOff,
  FaHdd, FaArrowUp, FaDownload, FaSearch, FaExclamationTriangle,
  FaPlay, FaPowerOff, FaEraser,
} from 'react-icons/fa';

const API = process.env.REACT_APP_API_BASE_URL;

const fmtBytes = (bytes) => {
  if (bytes === undefined || bytes === null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
};

const fmtUptime = (secs) => {
  if (secs === undefined || secs === null) return '—';
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${d}d ${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m`;
};

  const fmtOp = (n) => {
    if (n === undefined || n === null) return '—';
    if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
    if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
    return String(n);
  };

  const fmtElapsed = (ms) => {
    if (ms === undefined || ms === null) return '—';
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const h = Math.floor(m / 60);
    return h > 0 ? `${h}h ${m % 60}m ${String(s % 60).padStart(2, '0')}s` : `${m}m ${String(s % 60).padStart(2, '0')}s`;
  };

  // Average cores utilized = CPU time consumed ÷ wall-clock time.
  // 1.0 core = one CPU core fully busy for the whole run.
  const fmtCores = (cpuMs, wallMs) => {
    if (cpuMs === undefined || cpuMs === null || !wallMs || wallMs <= 0) return '—';
    const cores = cpuMs / wallMs;
    return cores < 10 ? `${cores.toFixed(2)} cores` : `${Math.round(cores)} cores`;
  };

const ServerLogs = () => {
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [tab, setTab] = useState('requests');

  // PM2 log viewer state
  const [procName, setProcName] = useState('HRMS');
  const [logType, setLogType] = useState('out');
  const [logLines, setLogLines] = useState(200);
  const [logs, setLogs] = useState('');
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logSearch, setLogSearch] = useState('');
  const [logAutoRefresh, setLogAutoRefresh] = useState(false);

  // Management action state
  const [actionState, setActionState] = useState({});
  const [actionLog, setActionLog] = useState([]);

  // PM2 restart events (parsed from the PM2 daemon log)
  const [restartEvents, setRestartEvents] = useState(null);

  // Tally sync CPU/RAM monitor state
  const [syncs, setSyncs] = useState(null);

  const timerRef = useRef(null);
  const logTimerRef = useRef(null);

  // ── Style helpers ──
  const cardBg = darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white';
  const text = darkMode ? 'text-gray-100' : 'text-gray-900';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const textLabel = darkMode ? 'text-gray-300' : 'text-gray-700';
  const theadBg = darkMode ? 'bg-gray-700' : 'bg-gray-50';
  const theadText = darkMode ? 'text-gray-300' : 'text-gray-500';
  const tbodyBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const tbodyDivide = darkMode ? 'divide-gray-700' : 'divide-gray-200';
  const tbodyText = darkMode ? 'text-gray-200' : 'text-gray-800';
  const tbodyMuted = darkMode ? 'text-gray-400' : 'text-gray-600';
  const divider = darkMode ? 'border-gray-700' : 'border-gray-200';
  const badge = darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700';
  const inputCls = `border rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 ${
    darkMode ? 'bg-gray-700 text-gray-100 border-gray-600' : 'bg-white text-gray-900 border-gray-300'
  }`;

  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'darkMode') setDarkMode(e.newValue === 'true');
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/server/status`);
      setStatus(res.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching server status:', err);
      toast.error(err.response?.data?.error || 'Failed to fetch server status');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSyncs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/server/syncs`);
      setSyncs(res.data);
    } catch (err) {
      console.error('Error fetching syncs:', err);
    }
  }, []);

  const fetchRestartEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/server/restarts`);
      setRestartEvents(res.data);
    } catch (err) {
      console.error('Error fetching restart events:', err);
    }
  }, []);

  useEffect(() => { fetchStatus(); fetchSyncs(); fetchRestartEvents(); }, [fetchStatus, fetchSyncs, fetchRestartEvents]);

  useEffect(() => {
    if (autoRefresh) {
      timerRef.current = setInterval(() => { fetchStatus(); fetchSyncs(); fetchRestartEvents(); }, 15000);
      return () => clearInterval(timerRef.current);
    }
  }, [autoRefresh, fetchStatus, fetchSyncs, fetchRestartEvents]);

  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const res = await axios.get(`${API}/server/logs/${encodeURIComponent(procName)}`, { params: { type: logType, lines: logLines } });
      setLogs(res.data);
    } catch (err) {
      setLogs('');
      toast.error(err.response?.data?.error || `Failed to fetch ${procName} logs`);
    } finally {
      setLoadingLogs(false);
    }
  }, [procName, logType, logLines]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    if (logAutoRefresh) {
      logTimerRef.current = setInterval(fetchLogs, 15000);
      return () => clearInterval(logTimerRef.current);
    }
  }, [logAutoRefresh, fetchLogs]);

  const downloadLogs = () => {
    if (!logs) return;
    const blob = new Blob([logs], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${procName}-${logType === 'out' ? 'stdout' : 'stderr'}-${new Date().toISOString().replace(/[:.]/g, '-')}.log`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Log file downloaded');
  };

  const runAction = async (key, endpoint, body, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setActionState(s => ({ ...s, [key]: { running: true } }));
    try {
      const res = await axios.post(`${API}${endpoint}`, body || {});
      const msg = res.data?.message || res.data?.error || 'Action completed';
      setActionLog(l => [
        { time: new Date(), key, message: msg, success: !!res.data?.success },
        ...l,
      ].slice(0, 20));
      toast.success(msg);
      setTimeout(fetchStatus, 4000);
    } catch (err) {
      const msg = err.response?.data?.error || 'Action failed';
      setActionLog(l => [{ time: new Date(), key, message: msg, success: false }, ...l].slice(0, 20));
      toast.error(msg);
    } finally {
      setActionState(s => ({ ...s, [key]: { running: false } }));
    }
  };

  const statusDot = (ok) => (
    <span className={`inline-block w-2.5 h-2.5 rounded-full ${ok ? 'bg-green-500' : 'bg-red-500'} mr-1.5`} />
  );

  const hrms = status?.pm2?.hrms;
  const pdf = status?.pm2?.processes?.find(p => (p.name || '').toUpperCase().includes('PDF'));
  const memoryTotal = status?.os?.totalMemory;
  const memoryFree = status?.os?.freeMemory;
  const swap = status?.os?.swap;
  const disk = status?.os?.disk;
  const mongo = status?.db?.mongo;

  const statCard = (icon, label, value, sub, accent) => (
    <div className={`rounded-lg shadow p-4 ${cardBg}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`${accent || 'text-blue-500'}`}>{icon}</span>
        <span className={`text-xs font-semibold uppercase tracking-wide ${textMuted}`}>{label}</span>
      </div>
      <div className={`text-xl font-bold ${text}`}>{value}</div>
      {sub && <div className={`text-xs mt-1 ${textMuted}`}>{sub}</div>}
    </div>
  );

  const filteredLogs = logSearch
    ? logs.split('\n').filter(l => l.toLowerCase().includes(logSearch.toLowerCase())).join('\n')
    : logs;

  const opCounters = mongo?.opcounters || {};

  const actionBtn = (key, icon, label, onClick, color = 'blue', sub) => (
    <div className={`flex flex-col items-start gap-1 ${cardBg ? '' : ''}`}>
      <div className="flex items-center gap-2 w-full">
        <button
          onClick={onClick}
          disabled={actionState[key]?.running}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded transition disabled:opacity-50 ${
            color === 'red' ? 'bg-red-600 hover:bg-red-700 text-white'
            : color === 'amber' ? 'bg-amber-600 hover:bg-amber-700 text-white'
            : color === 'green' ? 'bg-green-600 hover:bg-green-700 text-white'
            : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          <span className={actionState[key]?.running ? 'animate-spin' : ''}>{icon}</span>
          {actionState[key]?.running ? 'Working...' : label}
        </button>
        {sub && <span className={`text-xs ${textMuted}`}>{sub}</span>}
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-center justify-between mb-1">
        <h1 className={`text-2xl font-bold ${text}`}>Server Logs & Status</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setAutoRefresh(v => !v); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition ${
              autoRefresh ? 'bg-green-600 hover:bg-green-700 text-white' : badge
            }`}
          >
            {autoRefresh ? <><FaToggleOn /> Auto-refresh 15s</> : <><FaToggleOff /> Auto-refresh off</>}
          </button>
          <button
            onClick={fetchStatus}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded disabled:opacity-50 transition"
          >
            <FaSync className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>
      <p className={`text-sm mb-5 ${textMuted}`}>
        {lastUpdated ? `Last updated: ${lastUpdated.toLocaleTimeString()}` : 'Fetching server status...'}
      </p>

      {/* ═══ STAT CARDS ═══ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {statCard(
          <FaServer className="text-green-500" />, 'HRMS Process (PM2)',
          hrms ? hrms.status : 'N/A',
          hrms ? `CPU ${hrms.cpu}% · RAM ${fmtBytes(hrms.memory)} · ${hrms.instances} workers` : undefined,
          hrms?.status === 'online' ? null : 'text-red-500'
        )}
        {statCard(
          <FaMicrochip className="text-purple-500" />, 'CPU Load',
          status?.os?.loadAvg ? Number(status.os.loadAvg[0]).toFixed(2) : '—',
          status?.os?.loadAvg ? `1 / 5 / 15 min: ${status.os.loadAvg.map(v => Number(v).toFixed(2)).join(' / ')} · ${status.os.cpuCores} cores` : undefined,
          status?.os?.loadAvg && Number(status.os.loadAvg[0]) > status?.os?.cpuCores ? 'text-red-500' : null
        )}
        {statCard(
          <FaMemory className="text-yellow-500" />, 'System Memory',
          memoryTotal ? `${((1 - memoryFree / memoryTotal) * 100).toFixed(1)}% used` : '—',
          memoryTotal ? `${fmtBytes(memoryFree)} free of ${fmtBytes(memoryTotal)}` : undefined,
          memoryTotal && (memoryFree / memoryTotal) < 0.1 ? 'text-red-500' : null
        )}
        {statCard(
          <FaArrowUp className="text-green-500" />, 'Swap',
          swap?.total ? `${(((swap.total - swap.free) / swap.total) * 100).toFixed(1)}% used` : '—',
          swap?.total ? `${fmtBytes(swap.used)} used / ${fmtBytes(swap.total)}` : undefined,
          swap?.total && (swap.used / swap.total) > 0.9 ? 'text-red-500' : null
        )}
        {statCard(
          <FaHdd className="text-blue-500" />, 'Disk Usage',
          disk ? `${disk.percentUsed}% used` : '—',
          disk ? `${fmtBytes(disk.free)} free of ${fmtBytes(disk.total)}` : undefined,
          disk && disk.percentUsed > 90 ? 'text-red-500' : null
        )}
        {statCard(
          <FaNetworkWired className="text-blue-500" />, 'Active Requests',
          status?.activeRequests?.count !== undefined ? status.activeRequests.count : '—',
          status?.recentRequests ? `${status.recentRequests.length} recent` : undefined,
          status?.activeRequests?.count > 0 ? 'text-orange-500' : null
        )}
      </div>

      {/* ═══ TABS ═══ */}
      <div className={`rounded-lg shadow overflow-hidden mb-6 ${cardBg}`}>
        <div className={`px-5 py-2 border-b ${divider} flex items-center gap-4 overflow-x-auto`}>
          {[
            { key: 'requests', label: 'Active & Recent Requests', icon: <FaHistory className="text-blue-500" /> },
            { key: 'syncs', label: `Sync Monitor (${syncs?.active?.length || 0})`, icon: <FaMicrochip className="text-purple-500" /> },
            { key: 'database', label: 'Database', icon: <FaDatabase className="text-yellow-500" /> },
            { key: 'handlers', label: `Handlers (${status?.handlers?.total || 0})`, icon: <FaListUl className="text-green-500" /> },
            { key: 'logs', label: 'PM2 Logs', icon: <FaTerminal className="text-purple-500" /> },
            { key: 'manage', label: 'Management', icon: <FaPlay className="text-red-500" /> },
            { key: 'details', label: 'Technical Details', icon: <FaDatabase className="text-yellow-500" /> },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 py-2 px-1 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                tab === t.key
                  ? `${darkMode ? 'border-blue-400 text-blue-400' : 'border-blue-600 text-blue-600'}`
                  : `border-transparent ${textMuted} hover:${text}`
              }`}
            >
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        {/* ── ACTIVE & RECENT REQUESTS ── */}
        {tab === 'requests' && (
          <div>
            <div className="p-4">
              <h3 className={`text-sm font-semibold mb-2 ${textLabel}`}>
                Active (in-flight) requests
                {status?.activeRequests?.count > 0 && (
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-medium ${darkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-700'}`}>
                    {status.activeRequests.count}
                  </span>
                )}
              </h3>
              {!status?.activeRequests?.list?.length ? (
                <p className={`text-sm ${textMuted}`}>No requests currently in flight.</p>
              ) : (
                <table className="min-w-full">
                  <thead className={theadBg}>
                    <tr>
                      <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${theadText}`}>#</th>
                      <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${theadText}`}>Method</th>
                      <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${theadText}`}>Path</th>
                      <th className={`px-4 py-2 text-right text-xs font-medium uppercase ${theadText}`}>Elapsed (ms)</th>
                    </tr>
                  </thead>
                  <tbody className={`${tbodyBg} divide-y ${tbodyDivide}`}>
                    {status.activeRequests.list.map(r => (
                      <tr key={r.id}>
                        <td className={`px-4 py-2 text-xs ${tbodyMuted}`}>{r.id}</td>
                        <td className="px-4 py-2 text-xs font-medium text-blue-500">{r.method}</td>
                        <td className={`px-4 py-2 text-xs break-all ${tbodyText}`}>{r.path}</td>
                        <td className={`px-4 py-2 text-xs text-right ${r.elapsing > 5000 ? 'text-red-500 font-medium' : tbodyMuted}`}>
                          {r.elapsing} {r.elapsing > 5000 && <FaExclamationTriangle className="inline text-red-500" />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <h3 className={`text-sm font-semibold mt-5 mb-2 ${textLabel}`}>Recent requests (last {status?.recentRequests?.length || 0})</h3>
              {!status?.recentRequests?.length ? (
                <p className={`text-sm ${textMuted}`}>No recent requests yet.</p>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  <table className="min-w-full">
                    <thead className={theadBg}>
                      <tr>
                        <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${theadText}`}>Time</th>
                        <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${theadText}`}>Method</th>
                        <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${theadText}`}>Path</th>
                        <th className={`px-4 py-2 text-right text-xs font-medium uppercase ${theadText}`}>Status</th>
                        <th className={`px-4 py-2 text-right text-xs font-medium uppercase ${theadText}`}>Duration</th>
                      </tr>
                    </thead>
                    <tbody className={`${tbodyBg} divide-y ${tbodyDivide}`}>
                      {status.recentRequests.map(r => (
                        <tr
                          key={r.id}
                          className={r.duration > 5000 || r.status >= 400 ? `${darkMode ? 'bg-red-900 bg-opacity-20' : 'bg-red-50'}` : ''}
                        >
                          <td className={`px-4 py-2 text-xs whitespace-nowrap ${tbodyMuted}`}>{new Date(r.time).toLocaleTimeString()}</td>
                          <td className="px-4 py-2 text-xs font-medium text-blue-500">{r.method}</td>
                          <td className={`px-4 py-2 text-xs break-all ${tbodyText}`}>{r.path}</td>
                          <td className={`px-4 py-2 text-xs text-right font-medium ${r.status < 400 ? 'text-green-500' : 'text-red-500'}`}>{r.status}</td>
                          <td className={`px-4 py-2 text-xs text-right ${r.duration > 5000 ? 'text-red-500 font-medium' : tbodyMuted}`}>
                            {r.duration} ms {r.duration > 5000 && '⚠'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── SYNC MONITOR ── */}
        {tab === 'syncs' && (
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-sm font-semibold flex items-center gap-2 ${textLabel}`}>
                <FaMicrochip className="text-purple-500" /> Running Tally Syncs (live cores / RAM)
              </h3>
              <button
                onClick={() => axios.get(`${API}/server/syncs`).then(r => setSyncs(r.data)).catch(() => {})}
                className={`text-xs px-2 py-1 rounded ${badge}`}
              >
                <FaSync className="inline mr-1" />Refresh
              </button>
            </div>
            {!syncs?.active?.length ? (
              <p className={`text-sm ${textMuted}`}>No Tally syncs currently running.</p>
            ) : (
              <table className="min-w-full mb-6">
                <thead className={theadBg}>
                  <tr>
                    <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${theadText}`}>Sync</th>
                    <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${theadText}`}>Company</th>
                    <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${theadText}`}>Type</th>
                    <th className={`px-4 py-2 text-right text-xs font-medium uppercase ${theadText}`}>Elapsed</th>
                    <th className={`px-4 py-2 text-right text-xs font-medium uppercase ${theadText}`}>Cores utilized</th>
                    <th className={`px-4 py-2 text-right text-xs font-medium uppercase ${theadText}`}>Peak RAM</th>
                    <th className={`px-4 py-2 text-right text-xs font-medium uppercase ${theadText}`}>Worker</th>
                  </tr>
                </thead>
                <tbody className={`${tbodyBg} divide-y ${tbodyDivide}`}>
                  {syncs.active.map(s => (
                    <tr key={s.id}>
                      <td className={`px-4 py-2 text-xs font-medium ${tbodyText}`}>{s.name}</td>
                      <td className={`px-4 py-2 text-xs ${tbodyMuted}`}>{s.companyName || '—'}</td>
                      <td className={`px-4 py-2 text-xs ${tbodyMuted}`}>{s.type} ({s.syncType})</td>
                      <td className={`px-4 py-2 text-xs text-right ${tbodyMuted}`}>{fmtElapsed(s.elapsedMs)}</td>
                      <td className={`px-4 py-2 text-xs text-right font-medium ${s.cpuMs / s.elapsedMs > 1.5 ? 'text-red-500' : tbodyText}`}>
                        {s.cpuMs ? fmtCores(s.cpuMs, s.elapsedMs) : '—'}
                      </td>
                      <td className={`px-4 py-2 text-xs text-right font-medium ${s.peakRss > 1024 * 1024 * 1024 ? 'text-red-500' : tbodyText}`}>
                        {fmtBytes(s.peakRss)}
                      </td>
                      <td className={`px-4 py-2 text-xs text-right ${tbodyMuted}`}>w{s.worker}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <h3 className={`text-sm font-semibold mb-3 ${textLabel}`}>Recent runs (last {syncs?.recent?.length || 0})</h3>
            {!syncs?.recent?.length ? (
              <p className={`text-sm ${textMuted}`}>No completed runs recorded yet. Stats appear after the next sync completes.</p>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <table className="min-w-full">
                  <thead className={theadBg}>
                    <tr>
                      <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${theadText}`}>Sync</th>
                      <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${theadText}`}>Company</th>
                      <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${theadText}`}>Type</th>
                      <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${theadText}`}>Status</th>
                      <th className={`px-4 py-2 text-right text-xs font-medium uppercase ${theadText}`}>Finished</th>
                      <th className={`px-4 py-2 text-right text-xs font-medium uppercase ${theadText}`}>Time taken</th>
                      <th className={`px-4 py-2 text-right text-xs font-medium uppercase ${theadText}`}>Cores utilized</th>
                      <th className={`px-4 py-2 text-right text-xs font-medium uppercase ${theadText}`}>Peak RAM</th>
                    </tr>
                  </thead>
                  <tbody className={`${tbodyBg} divide-y ${tbodyDivide}`}>
                    {syncs.recent.map(s => {
                      const st = s.lastRunStats;
                      const cores = st && st.durationMs > 0 ? st.cpuMs / st.durationMs : 0;
                      const heavy = st && (cores > 1.5 || st.peakRss > 1024 * 1024 * 1024);
                      return (
                        <tr key={s._id} className={heavy ? `${darkMode ? 'bg-red-900 bg-opacity-20' : 'bg-red-50'}` : ''}>
                          <td className={`px-4 py-2 text-xs font-medium ${tbodyText}`}>{s.name}</td>
                          <td className={`px-4 py-2 text-xs ${tbodyMuted}`}>{s.companyName || '—'}</td>
                          <td className={`px-4 py-2 text-xs ${tbodyMuted}`}>{s.TallySyncType} ({s.syncTypeData})</td>
                          <td className="px-4 py-2 text-xs">{statusDot(s.status === 'completed')}<span className={s.status === 'completed' ? 'text-green-500' : 'text-red-500'}>{s.status}</span></td>
                          <td className={`px-4 py-2 text-xs whitespace-nowrap text-right ${tbodyMuted}`}>
                            {s.lastRunAt ? new Date(s.lastRunAt).toLocaleTimeString() : '—'}
                          </td>
                          <td className={`px-4 py-2 text-xs text-right ${tbodyMuted}`}>{st ? fmtElapsed(st.durationMs) : '—'}</td>
                          <td className={`px-4 py-2 text-xs text-right font-medium ${cores > 1.5 ? 'text-red-500' : tbodyText}`}>
                            {st ? fmtCores(st.cpuMs, st.durationMs) : '—'}
                          </td>
                          <td className={`px-4 py-2 text-xs text-right font-medium ${st && st.peakRss > 1024 * 1024 * 1024 ? 'text-red-500' : tbodyText}`}>
                            {st ? fmtBytes(st.peakRss) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── DATABASE ── */}
        {tab === 'database' && (
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`rounded-lg border ${divider} p-4`}>
              <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${textLabel}`}><FaDatabase className="text-yellow-500" /> MongoDB</h3>
              <ul className={`space-y-2 text-sm ${tbodyText}`}>
                <li className="flex justify-between">
                  <span className={textMuted}>Connection state</span>
                  <span className="font-medium flex items-center">{statusDot(status?.db?.readyState === 'connected')} {status?.db?.readyState}</span>
                </li>
                <li className="flex justify-between"><span className={textMuted}>Version</span><span className="font-medium">{mongo?.version}</span></li>
                <li className="flex justify-between"><span className={textMuted}>Uptime</span><span className="font-medium">{fmtUptime(mongo?.uptime)}</span></li>
                <li className="flex justify-between"><span className={textMuted}>Resident memory</span><span className="font-medium">{fmtBytes(mongo?.mem?.residentMB * 1024 * 1024)}</span></li>
                <li className="flex justify-between"><span className={textMuted}>Virtual memory</span><span className="font-medium">{fmtBytes(mongo?.mem?.virtualMB * 1024 * 1024)}</span></li>
              </ul>
            </div>
            <div className={`rounded-lg border ${divider} p-4`}>
              <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${textLabel}`}><FaNetworkWired className="text-blue-500" /> Connections</h3>
              {mongo?.connections ? (
                <>
                  <div className="flex items-end gap-4 mb-3">
                    <div>
                      <div className={`text-3xl font-bold ${text}`}>{mongo.connections.current}</div>
                      <div className={`text-xs ${textMuted}`}>current</div>
                    </div>
                    <div>
                      <div className={`text-lg font-semibold ${text}`}>{mongo.connections.active}</div>
                      <div className={`text-xs ${textMuted}`}>active</div>
                    </div>
                    <div>
                      <div className={`text-lg font-semibold ${text}`}>{fmtOp(mongo.connections.available)}</div>
                      <div className={`text-xs ${textMuted}`}>available</div>
                    </div>
                  </div>
                  <div className="w-full h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                    <div
                      className={`h-full ${mongo.connections.current > mongo.connections.available * 0.1 ? 'bg-red-500' : 'bg-green-500'}`}
                      style={{ width: `${Math.min(100, (mongo.connections.current / Math.max(1, mongo.connections.current + mongo.connections.available)) * 100)}%` }}
                    />
                  </div>
                  <p className={`text-xs mt-1 ${textMuted}`}>Pool: {mongo.connections.current} of {fmtOp(mongo.connections.current + mongo.connections.available)}</p>
                </>
              ) : (
                <p className={`text-sm ${textMuted}`}>No connection data.</p>
              )}
            </div>
            <div className={`rounded-lg border ${divider} p-4 md:col-span-2`}>
              <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${textLabel}`}><FaServer className="text-green-500" /> Operation Counters (since mongod start)</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { label: 'Insert', value: opCounters.insert },
                  { label: 'Query', value: opCounters.query },
                  { label: 'Update', value: opCounters.update },
                  { label: 'Delete', value: opCounters.delete },
                  { label: 'GetMore', value: opCounters.getmore },
                  { label: 'Command', value: opCounters.command },
                ].map(o => (
                  <div key={o.label} className={`rounded-lg border ${divider} p-3 text-center`}>
                    <div className={`text-lg font-bold ${text}`}>{fmtOp(o.value)}</div>
                    <div className={`text-xs uppercase tracking-wide ${textMuted}`}>{o.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── HANDLERS / ROUTES ── */}
        {tab === 'handlers' && (
          <div className="p-4">
            <div className="flex items-center gap-4 mb-3">
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${badge}`}>Routes: {status?.handlers?.total || 0}</span>
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${badge}`}>Middleware layers: {status?.handlers?.layers || 0}</span>
            </div>
            {!status?.handlers?.routes?.length ? (
              <p className={`text-sm ${textMuted}`}>No registered routes found.</p>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <table className="min-w-full">
                  <thead className={theadBg}>
                    <tr>
                      <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${theadText}`}>Method</th>
                      <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${theadText}`}>Path</th>
                    </tr>
                  </thead>
                  <tbody className={`${tbodyBg} divide-y ${tbodyDivide}`}>
                    {status.handlers.routes.map((r, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 text-xs font-medium text-blue-500 whitespace-nowrap">{r.method}</td>
                        <td className={`px-4 py-2 text-xs break-all ${tbodyText}`}>{r.path}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── PM2 LOGS ── */}
        {tab === 'logs' && (
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <select
                value={procName}
                onChange={e => setProcName(e.target.value)}
                className={inputCls}
              >
                {(status?.pm2?.processes?.length
                  ? [...new Set(status.pm2.processes.map(p => p.name))].filter(n => n !== 'pm2-logrotate')
                  : ['HRMS']
                ).map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <select
                value={logType}
                onChange={e => setLogType(e.target.value)}
                className={inputCls}
              >
                <option value="out">stdout</option>
                <option value="error">stderr</option>
              </select>
              <select
                value={logLines}
                onChange={e => setLogLines(Number(e.target.value))}
                className={inputCls}
              >
                {[100, 200, 500, 1000].map(n => <option key={n} value={n}>{n} lines</option>)}
              </select>
              <div className="relative">
                <FaSearch className="absolute left-2.5 top-2 text-xs text-gray-400" />
                <input
                  value={logSearch}
                  onChange={e => setLogSearch(e.target.value)}
                  placeholder="Filter..."
                  className={`${inputCls} pl-8`}
                />
              </div>
              <button
                onClick={fetchLogs}
                disabled={loadingLogs}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded disabled:opacity-50 transition"
              >
                <FaSync className={loadingLogs ? 'animate-spin' : ''} /> Reload
              </button>
              <button
                onClick={downloadLogs}
                disabled={!logs}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded disabled:opacity-50 transition"
              >
                <FaDownload /> Download
              </button>
              <button
                onClick={() => setLogAutoRefresh(v => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition ${
                  logAutoRefresh ? 'bg-green-600 hover:bg-green-700 text-white' : badge
                }`}
              >
                {logAutoRefresh ? <><FaToggleOn /> Live 15s</> : <><FaToggleOff /> Live off</>}
              </button>
            </div>
            <pre className={`text-xs leading-relaxed p-4 rounded-lg overflow-x-auto max-h-96 overflow-y-auto font-mono ${darkMode ? 'bg-gray-900 text-green-400' : 'bg-gray-100 text-gray-800'}`}>
              {loadingLogs ? 'Loading logs...' : (filteredLogs || 'No log output available.')}
            </pre>
          </div>
        )}

        {/* ── MANAGEMENT ── */}
        {tab === 'manage' && (
          <div className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <FaPlay className="text-red-500" />
              <h3 className={`text-sm font-semibold ${textLabel}`}>Application Management</h3>
            </div>
            <p className={`text-xs mb-4 ${textMuted}`}>
              Reload = rolling, zero-downtime restart. Restart = hard restart of every worker (brief gap).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className={`rounded-lg border ${divider} p-4`}>
                <h4 className={`text-sm font-semibold mb-3 ${textLabel}`}>HRMS ({hrms?.instances || '?'} workers)</h4>
                <div className="flex flex-col gap-3">
                  {actionBtn('reload-hrms', <FaSync />, 'Reload HRMS (rolling)', () => runAction(
                    'reload-hrms', '/server/pm2/HRMS/reload', null,
                    'Rolling zero-downtime reload of all HRMS workers?'
                  ))}
                  {actionBtn('restart-hrms', <FaPowerOff />, 'Restart HRMS', () => runAction(
                    'restart-hrms', '/server/pm2/HRMS/restart', null,
                    'Hard restart ALL HRMS workers now? Requests in flight may be dropped.'
                  ), 'amber')}
                  {hrms && (
                    <div className={`text-xs ${textMuted}`}>
                      Status: <span className="font-medium text-green-500">{hrms.status}</span> · {hrms.restarts} restarts
                    </div>
                  )}
                </div>
              </div>
              <div className={`rounded-lg border ${divider} p-4`}>
                <h4 className={`text-sm font-semibold mb-3 ${textLabel}`}>PDF Generator ({pdf?.instances || '?'} workers)</h4>
                <div className="flex flex-col gap-3">
                  {actionBtn('reload-pdf', <FaSync />, 'Reload PDF (rolling)', () => runAction(
                    'reload-pdf', '/server/pm2/PDF_GENERATOR/reload', null,
                    'Rolling zero-downtime reload of PDF workers?'
                  ))}
                  {actionBtn('restart-pdf', <FaPowerOff />, 'Restart PDF', () => runAction(
                    'restart-pdf', '/server/pm2/PDF_GENERATOR/restart', null,
                    'Hard restart ALL PDF workers now?'
                  ), 'amber')}
                  {pdf && (
                    <div className={`text-xs ${textMuted}`}>
                      Status: <span className="font-medium text-green-500">{pdf.status}</span> · {pdf.restarts} restarts
                    </div>
                  )}
                </div>
              </div>
              <div className={`rounded-lg border ${divider} p-4`}>
                <h4 className={`text-sm font-semibold mb-3 ${textLabel}`}>System</h4>
                <div className="flex flex-col gap-3">
                  {actionBtn('restart-services', <FaEraser />, 'Restart services (nginx + MongoDB + PM2)', () => runAction(
                    'restart-services', '/server/restart-services', null,
                    'Restart nginx, MongoDB and ALL PM2 processes? The app will be briefly offline.'
                  ), 'amber')}
                </div>
              </div>
            </div>

            {actionLog.length > 0 && (
              <div className={`mt-4 rounded-lg border ${divider} p-4`}>
                <h4 className={`text-sm font-semibold mb-2 ${textLabel}`}>Recent actions</h4>
                <div className="max-h-56 overflow-y-auto space-y-1.5">
                  {actionLog.map((a, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1 ${a.success ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className={`whitespace-nowrap ${textMuted}`}>{a.time.toLocaleTimeString()}</span>
                      <span className={`break-all ${a.success ? 'text-green-500' : 'text-red-500'}`}>{a.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className={`mt-4 rounded-lg border ${divider} p-4`}>
              <div className="flex items-center justify-between mb-2">
                <h4 className={`text-sm font-semibold ${textLabel}`}>PM2 restart events (from daemon log)</h4>
                <button
                  onClick={fetchRestartEvents}
                  className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded transition ${badge} hover:opacity-80`}
                >
                  <FaSync /> Refresh
                </button>
              </div>
              {restartEvents?.counters && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {Object.entries(restartEvents.counters).map(([name, c]) => (
                    <span key={name} className={`px-2 py-1 rounded text-xs ${badge}`}>
                      {name}: <span className="font-semibold">{c.restarts}</span> restarts
                      {c.unstable > 0 && <span className="text-red-500"> · {c.unstable} unstable</span>}
                    </span>
                  ))}
                </div>
              )}
              <div className="max-h-72 overflow-y-auto space-y-1">
                {restartEvents?.events?.length ? restartEvents.events.map((ev, i) => {
                  const kindCls = ev.kind === 'graceful'
                    ? 'bg-green-500'
                    : ev.kind === 'crash'
                      ? 'bg-red-500'
                      : ev.kind === 'killed'
                        ? 'bg-purple-500'
                        : 'bg-yellow-500';
                  const kindLabel = ev.kind === 'graceful'
                    ? 'Graceful'
                    : ev.kind === 'crash'
                      ? 'CRASH'
                      : ev.kind === 'killed'
                        ? 'Killed'
                        : 'Restarted';
                  return (
                    <div key={i} className="flex items-start gap-2 text-xs py-0.5">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1 ${kindCls}`} title={kindLabel} />
                      <span className={`whitespace-nowrap ${textMuted}`}>
                        {ev.time ? ev.time.slice(0, 19).replace('T', ' ') : '—'}
                      </span>
                      <span className={`font-medium ${tbodyText}`}>{ev.app}</span>
                      <span className={`${ev.kind === 'graceful' ? 'text-green-500' : ev.kind === 'restart' ? 'text-yellow-500' : 'text-red-500'} break-all`}>
                        {ev.oom && '⚠ '}{ev.reason}
                      </span>
                      {ev.backOnlineAt && (
                        <span className={`${textMuted} ml-auto`}>back online {ev.backOnlineAt.slice(0, 19).replace('T', ' ')}</span>
                      )}
                    </div>
                  );
                }) : (
                  <p className={`text-xs ${textMuted}`}>No restart events found in the PM2 daemon log.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── TECHNICAL DETAILS ── */}
        {tab === 'details' && (
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`rounded-lg border ${divider} p-4`}>
              <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${textLabel}`}><FaServer className="text-green-500" /> System</h3>
              <ul className={`space-y-2 text-sm ${tbodyText}`}>
                <li className="flex justify-between"><span className={textMuted}>Hostname</span><span className="font-medium">{status?.os?.hostname}</span></li>
                <li className="flex justify-between"><span className={textMuted}>Platform</span><span className="font-medium">{status?.os?.platform} {status?.os?.arch} {status?.os?.release}</span></li>
                <li className="flex justify-between"><span className={textMuted}>Uptime</span><span className="font-medium">{fmtUptime(status?.os?.uptime)}</span></li>
                <li className="flex justify-between"><span className={textMuted}>CPU cores</span><span className="font-medium">{status?.os?.cpuCores}</span></li>
                <li className="flex justify-between"><span className={textMuted}>Load avg</span><span className="font-medium">{status?.os?.loadAvg?.map(v => Number(v).toFixed(2)).join(' / ')}</span></li>
                <li className="flex justify-between"><span className={textMuted}>Memory</span><span className="font-medium">{fmtBytes(memoryFree)} free / {fmtBytes(memoryTotal)}</span></li>
                <li className="flex justify-between"><span className={textMuted}>Swap</span><span className="font-medium">{fmtBytes(swap?.free)} free / {fmtBytes(swap?.total)}</span></li>
                <li className="flex justify-between"><span className={textMuted}>Disk</span><span className="font-medium">{fmtBytes(disk?.free)} free / {fmtBytes(disk?.total)} ({disk?.percentUsed}% used)</span></li>
              </ul>
            </div>
            <div className={`rounded-lg border ${divider} p-4`}>
              <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${textLabel}`}><FaDatabase className="text-yellow-500" /> Application</h3>
              <ul className={`space-y-2 text-sm ${tbodyText}`}>
                <li className="flex justify-between"><span className={textMuted}>Node version</span><span className="font-medium">{status?.os?.nodeVersion}</span></li>
                <li className="flex justify-between"><span className={textMuted}>PID / Instance</span><span className="font-medium">{status?.process?.pid} ({status?.process?.instance})</span></li>
                <li className="flex justify-between"><span className={textMuted}>Process uptime</span><span className="font-medium">{fmtUptime(status?.process?.uptime)}</span></li>
                <li className="flex justify-between"><span className={textMuted}>RSS</span><span className="font-medium">{fmtBytes(status?.process?.memory?.rss)}</span></li>
                <li className="flex justify-between"><span className={textMuted}>Heap used</span><span className="font-medium">{fmtBytes(status?.process?.memory?.heapUsed)} / {fmtBytes(status?.process?.memory?.heapTotal)}</span></li>
                <li className="flex justify-between"><span className={textMuted}>MongoDB</span><span className="font-medium flex items-center">{statusDot(status?.db?.readyState === 'connected')} {status?.db?.readyState}</span></li>
                <li className="flex justify-between"><span className={textMuted}>MongoDB version</span><span className="font-medium">{mongo?.version}</span></li>
              </ul>
            </div>
            <div className={`rounded-lg border ${divider} p-4`}>
              <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${textLabel}`}><FaMicrochip className="text-purple-500" /> HRMS Process (PM2)</h3>
              {!hrms ? (
                <p className={`text-sm ${textMuted}`}>No PM2 process named HRMS found.</p>
              ) : (
                <ul className={`space-y-2 text-sm ${tbodyText}`}>
                  <li className="flex justify-between"><span className={textMuted}>Status</span><span className="font-medium flex items-center">{statusDot(hrms.status === 'online')} {hrms.status}</span></li>
                  <li className="flex justify-between"><span className={textMuted}>CPU</span><span className="font-medium">{hrms.cpu}%</span></li>
                  <li className="flex justify-between"><span className={textMuted}>Memory</span><span className="font-medium">{fmtBytes(hrms.memory)}</span></li>
                  <li className="flex justify-between"><span className={textMuted}>Uptime</span><span className="font-medium">{fmtUptime((Date.now() - hrms.uptime) / 1000)}</span></li>
                  <li className="flex justify-between"><span className={textMuted}>Restarts</span><span className="font-medium">{hrms.restarts}</span></li>
                  <li className="flex justify-between"><span className={textMuted}>Instances / Mode</span><span className="font-medium">{hrms.instances} ({hrms.execMode})</span></li>
                  <li className="flex justify-between"><span className={textMuted}>Node (PM2)</span><span className="font-medium">{hrms.nodeVersion}</span></li>
                </ul>
              )}
            </div>
            <div className={`rounded-lg border ${divider} p-4`}>
              <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${textLabel}`}><FaNetworkWired className="text-blue-500" /> All PM2 Processes</h3>
              {!status?.pm2?.processes?.length ? (
                <p className={`text-sm ${textMuted}`}>No PM2 processes found.</p>
              ) : (
                <table className="min-w-full">
                  <thead className={theadBg}>
                    <tr>
                      <th className={`px-3 py-2 text-left text-xs font-medium uppercase ${theadText}`}>Name</th>
                      <th className={`px-3 py-2 text-left text-xs font-medium uppercase ${theadText}`}>Status</th>
                      <th className={`px-3 py-2 text-right text-xs font-medium uppercase ${theadText}`}>CPU%</th>
                      <th className={`px-3 py-2 text-right text-xs font-medium uppercase ${theadText}`}>RAM</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${tbodyDivide}`}>
                    {status.pm2.processes.map(p => (
                      <tr key={p.id}>
                        <td className={`px-3 py-2 text-xs font-medium ${tbodyText}`}>{p.name}</td>
                        <td className="px-3 py-2 text-xs">{statusDot(p.status === 'online')}<span className={p.status === 'online' ? 'text-green-500' : 'text-red-500'}>{p.status}</span></td>
                        <td className={`px-3 py-2 text-xs text-right ${tbodyMuted}`}>{p.cpu}</td>
                        <td className={`px-3 py-2 text-xs text-right ${tbodyMuted}`}>{fmtBytes(p.memory)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServerLogs;
